import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert, Box, Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import TextInput from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { useAsyncCan } from 'modules/core/lib/useAsyncCan'
import legalEntitiesCollection from 'modules/core/store/collections/legalEntities'

import LevelsInput from 'modules/job_catalog/components/LevelsInput'
import jobLevelsCollection from 'modules/job_catalog/store/collections/levels'
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'

const Component = () => {
  const { value: canCreateJob } = useAsyncCan('job_catalog.create_job')

  return (
    <AuthorizeUser authorizeFn={() => canCreateJob}>
      <NewJobRoleModalPage />
    </AuthorizeUser>
  )
}

const NewJobRoleModalPage = observer(() => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { company } = useCurrentSession()
  const params = useSearchParams<{
    fallbackRoute: string
  }>()
  const legalEntitiesIds = legalEntitiesCollection.map((x) => x.attributes.id)
  const [legalEntities, setLegalEntities] = useState<number[]>(legalEntitiesIds)
  const [levelNames, setLevelNames] = useState<string[]>([])
  const form = useLazyRef(() => new Form({ name: '' }, { name: 'string' })).current
  const {
    createNewJobSuccess,
    createNewJobCancel,
    createNewJobSelectLegalEntitySubmit,
    createNewJobNameRoleSubmit,
    createNewJobLegalEntitiesSubmit,
  } = trackingEvents.jobCatalog

  const handleSubmit = async () => {
    const name = form.get('name').value.replace(/\s+/g, ' ')
    const { success, value } = await asyncAction(() =>
      jobRolesCollection.rpc('initialize_role', {
        company_id: company.id,
        name,
        legal_entities_ids: legalEntities,
        level_names: levelNames,
      })
    )

    if (success) {
      await jobRolesCollection.fetch()
      await jobLevelsCollection.fetch()
      track(createNewJobSuccess)

      const id = value.id
      if (params?.fallbackRoute) {
        navigate(`${params?.fallbackRoute}?createdRoleId=${id}`)
      } else {
        navigate(navigation.employees.jobCatalog.role.settings.fullPath({ id: id }))
      }
    }
  }

  const handleClose = () => {
    navigate(-1)
    track(createNewJobCancel)
  }

  const handleLegalEntitiesChange = (ids: number[]) => {
    setLegalEntities(ids)
    track(createNewJobSelectLegalEntitySubmit)
  }

  const handleNameChange = () => track(createNewJobNameRoleSubmit)

  const handleToggleLegalEntitiesSelect = (isOpen: boolean) => {
    if (!isOpen) track(createNewJobLegalEntitiesSubmit)
  }

  const legalEntitiesOptions = legalEntitiesCollection.map((x) => {
    const { id, legal_name } = x.attributes

    return (
      <Selector.Option key={id} value={id} label={legal_name} searchValue={legal_name}>
        {legal_name}
      </Selector.Option>
    )
  })

  const levelAlert = i18n.t('job_catalog.job_levels_description')

  return (
    <Modal
      illustration="input-field"
      title={i18n.t('job_catalog.new_job')}
      description={i18n.t('job_catalog.new_job_description')}
      onSubmit={handleSubmit}
      onClose={handleClose}
      footer={
        <>
          <Button type="secondary" onClick={handleClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            disabled={!form.isComplete || !legalEntities.length}
            submit
            extra={{ 'data-intercom-target': 'job-catalog-new-job-role-submit-button' }}
          >
            {i18n.t('common.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <TextInput
          onChange={handleNameChange}
          label={i18n.t('job_catalog.role_name')}
          field={form.get('name')}
          mandatory
          extra={{ 'data-intercom-target': 'job-catalog-new-job-role-name-input' }}
        />
        <Selector.Popover
          label={i18n.t('common.legal_entities')}
          headerLabel={i18n.t('common.name')}
          blankLabel={i18n.t('common.empty')}
          allSelectedLabel={i18n.t('common.all')}
          placeholder={i18n.t('common.select')}
          icon="office"
          value={legalEntities}
          onChange={(ids) => handleLegalEntitiesChange(ids as number[])}
          onToggle={handleToggleLegalEntitiesSelect}
          searchPlaceholder={i18n.t('common.search')}
          isMultiSelect
          mandatory
          extra={{ 'data-intercom-target': 'job-catalog-new-job-legal-entities-selector' }}
        >
          {legalEntitiesOptions}
        </Selector.Popover>
        <LevelsInput onChange={setLevelNames} />
      </Box>
      <Alert color="note">{levelAlert}</Alert>
    </Modal>
  )
})

export default Component
